import React, { Component, Dispatch } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { SofaPanel, SofaButton } from '@sofology/sofologycomponents';

import './BasketPage.scss';

import { AppState } from 'store';
import {
    initBasketPage,
    InitBasketPageFunc,
    setShowReservationWarning,
    SetShowReservationWarningFunc,
} from 'store/basket/actions';
import { routes } from 'routes';
import { BasketPaymentTypeSelectorType } from './BasketPaymentTypeSelector/BasketPaymentTypeSelector';
import { BasketDeliveryCostType } from './BasketDeliveryCost/BasketDeliveryCost';
import { BasketDetailsModel, StockTypes } from 'models/Basket';
import {
    BasketBar,
    BasketOrderTotal,
    Loader,
    BasketModal,
    InvalidItemsModal,
} from 'components';
import {
    externalComponentsHelper,
    generateDispatchToPropsMap,
    generateSofaLink,
    goToExternalUrl,
    GtmCheckoutSteps,
    getRootLink,
    scrollHelper,
} from 'helpers';
import {
    BasketDonation,
    BasketItemList,
    EmptyBasket,
    BasketUpsellAccordion,
    SofashieldPolicy,
    BasketDeliveryCost,
    BasketPaymentTypeSelector,
} from 'pages/BasketPage';
import {
    preprocessRedirectToNextStep,
    PreprocessRedirectToNextStepFunc,
} from 'store/checkout/actions';

interface OwnProps {}

interface StateProps {
    isDeliveryCostCalculating: boolean;
    isBasketPageInitializing: boolean;
    isDonationLoading: boolean;
    basket: BasketDetailsModel | null;
    waitsForChoosingDeliveryType: boolean;
    shouldShowReservationWarning: boolean;
    isCartValid: boolean;
    isValidatingCart: boolean;
    isAllInvalidItemsRemovingProcessing: boolean;
}

interface DispatchProps {
    initBasketPage: InitBasketPageFunc;
    preprocessRedirectToNextStep: PreprocessRedirectToNextStepFunc;
    setShowReservationWarning: SetShowReservationWarningFunc;
}

class BasketPage extends Component<
StateProps & OwnProps & DispatchProps & RouteComponentProps
> {
    private sofashieldPolicy = React.createRef<SofashieldPolicy>();
    private deliveryCost = React.createRef<BasketDeliveryCostType>();
    private paymentMethod = React.createRef<BasketPaymentTypeSelectorType>();
    private basketItems = React.createRef<HTMLDivElement>();

    private elementsForValidation: any[] = [
        this.sofashieldPolicy,
        this.deliveryCost,
        this.paymentMethod,
    ];

    componentDidMount() {
        externalComponentsHelper.showUmbracoHeaderFooter();
        this.props.initBasketPage(true);
    }

    componentDidUpdate(prevProps: StateProps) {
        if (
            !prevProps.isAllInvalidItemsRemovingProcessing &&
            this.props.isAllInvalidItemsRemovingProcessing
        ) {
            scrollHelper.scrollToTopOfElement(this.basketItems);
        }
    }

    handleContinueShoppingClick = () => {
        let productsWithRanges = this.props.basket?.entries.filter(
            p => p.range && p.hasStockType(StockTypes.Default)
        );

        let url = getRootLink();
        if (productsWithRanges?.length) {
            let lastAddedProduct =
                productsWithRanges[productsWithRanges.length - 1];
            url = generateSofaLink(lastAddedProduct).toLowerCase();
        }

        goToExternalUrl(url);
    };

    handleCheckoutClick = () => {
        let hasErrors = false;
        let firstElement = null;

        for (let element of this.elementsForValidation) {
            if (element?.current && !element.current.validate()) {
                if (!hasErrors) {
                    firstElement = element;
                }

                hasErrors = true;
            }
        }

        if (this.props.basket!.isDwellMixedBasket()) {
            hasErrors = true;
        }

        if (hasErrors) {
            firstElement?.current.scrollTo();
        } else {
            this.props.preprocessRedirectToNextStep(
                () => this.props.history.push(routes.deliveryDetails.path),
                GtmCheckoutSteps.DeliveryDetails
            );
        }
    };

    handleReservationWarningClick = () => {
        this.props.setShowReservationWarning(false);
    };

    render(): JSX.Element {
        const { basket, isBasketPageInitializing } = this.props;

        return (
            <div className='basket-page'>
                <BasketBar
                    cartItemsCount={basket ? basket.totalBasketItemsCount : 0}
                    loading={isBasketPageInitializing}
                />
                {this.renderBasketContent()}
            </div>
        );
    }

    renderBasketContent(): JSX.Element {
        const {
            isBasketPageInitializing,
            basket,
            shouldShowReservationWarning,
        } = this.props;

        if (!basket && isBasketPageInitializing) {
            return (
                <div className='basket-page-loader'>
                    <Loader show={isBasketPageInitializing} />
                </div>
            );
        }

        if (basket && !basket.isEmpty()) {
            return (
                <div className='basket-page__content-wrapper'>
                    <div className='basket-page__content'>
                        <div
                            className='basket-page__content__basket-items'
                            ref={this.basketItems}
                        >
                            <BasketItemList />
                        </div>
                        <div className='basket-page__content__upsells'>
                            <SofaPanel>
                                <BasketUpsellAccordion />
                            </SofaPanel>
                            {basket.hasSofashields() && (
                                <SofashieldPolicy ref={this.sofashieldPolicy} />
                            )}
                        </div>
                        {this.renderCheckoutContent()}
                        <BasketModal
                            open={shouldShowReservationWarning}
                            onClick={this.handleReservationWarningClick}
                        />
                        <InvalidItemsModal isBasketPage={true} />
                    </div>
                </div>
            );
        }

        return (
            <>
                <BasketModal
                    open={shouldShowReservationWarning}
                    onClick={this.handleReservationWarningClick}
                />
                <EmptyBasket />
            </>
        );
    }

    renderCheckoutContent(): JSX.Element {
        const { basket, isDonationLoading, waitsForChoosingDeliveryType } =
            this.props;
        const shouldIncludeDelivery = Boolean(
            basket!.deliveryCost && !waitsForChoosingDeliveryType
        );
        const isDeliveryCostFree = Boolean(basket!.deliveryCost?.value === 0);

        return (
            <div className='basket-page__content__checkout'>
                <div className='checkout-donation'>
                    <div className='basket-page__content__checkout__donate'>
                        <BasketDonation />
                    </div>
                    {isDonationLoading && this.renderDonationLoader()}
                </div>
                <div className='basket-page__content__checkout__checkout'>
                    <div className='basket-checkout-panels'>
                        <SofaPanel>
                            <BasketDeliveryCost ref={this.deliveryCost} />
                        </SofaPanel>
                        <SofaPanel>
                            <BasketOrderTotal
                                discounts={basket!.totalDiscounts}
                                totalPriceWithTax={basket!.totalPriceWithTax}
                                includeDelivery={shouldIncludeDelivery}
                                isDeliveryCostFree={isDeliveryCostFree}
                            />
                        </SofaPanel>
                        <SofaPanel>
                            <BasketPaymentTypeSelector
                                ref={this.paymentMethod}
                            />
                        </SofaPanel>
                    </div>
                    {this.renderCheckoutButton()}
                    {this.renderContinueShoppingLink()}
                </div>
            </div>
        );
    }

    renderDonationLoader(): JSX.Element {
        return (
            <div className='basket-page__content__checkout__loader'>
                <Loader show={this.props.isDonationLoading} />
            </div>
        );
    }

    renderCheckoutButton(): JSX.Element {
        const {
            isDeliveryCostCalculating,
            isCartValid,
            isValidatingCart
        } = this.props;

        const isCheckoutDisabled =
            isDeliveryCostCalculating ||
            isValidatingCart ||
            !isCartValid;

        return (
            <div className='basket-page__checkout-wrapper'>
                <SofaButton
                    text='Checkout'
                    variant='primary'
                    disabled={isCheckoutDisabled}
                    onClick={this.handleCheckoutClick}
                />
                {this.props.basket!.isDwellMixedBasket() && (
                    <div className='basket-page__checkout-wrapper__error'>
                        To purchase Dwell products, the basket must only contain
                        Dwell products
                    </div>
                )}
            </div>
        );
    }

    renderContinueShoppingLink(): JSX.Element {
        return (
            <div className='basket-page__continue-shopping-wrapper'>
                <SofaButton
                    text='Continue shopping'
                    variant='small'
                    onClick={this.handleContinueShoppingClick}
                ></SofaButton>
            </div>
        );
    }

    getCurrentLocation(): string {
        return this.props.location.pathname;
    }
}

function mapStateToProps(state: AppState): StateProps {
    return {
        isDeliveryCostCalculating: state.basketState.isCalculatingDeliveryCost,
        isBasketPageInitializing: state.basketState.isBasketPageInitializing,
        isDonationLoading: state.basketState.isDonationLoading,
        basket: state.basketState.basket,
        waitsForChoosingDeliveryType:
            state.basketState.waitsForChoosingDeliveryType,
        shouldShowReservationWarning:
            state.basketState.shouldShowReservationWarning,
        isValidatingCart: state.basketState.isValidatingCart,
        isCartValid: state.basketState.validationResult.isValid,
        isAllInvalidItemsRemovingProcessing: state.basketState.isAllInvalidItemsRemovingProcessing
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(dispatch, [
        initBasketPage,
        preprocessRedirectToNextStep,
        setShowReservationWarning,
    ]);
}

export default connect(mapStateToProps, mapDispatchToProps)(BasketPage);
